import { useEffect, useState } from "react";
import { ArrowUpRightIcon, ChromeIcon, PuzzleIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import {
    extensionSeen,
    hadExtension,
    rememberExtension,
    useExtensionPresent,
} from "@/lib/extensionPresence";
import { EXTENSION_STORE_LABELS, EXTENSION_STORES, extensionStores } from "@/lib/extensionStores";
import { STRIP_HEADLINE, STRIP_LINE, STRIP_PILL } from "@/lib/landing";
import { OUTLINE_BUTTON } from "./styles";

/**
 * How long a browser that had the extension waits for it to say so. Its
 * app page script runs at document idle, after the first paint.
 */
const WAIT_MS = 1_500;

/**
 * Get the extension: one slim strip over the board, for everyone, that
 * leads with the AI tailoring, and offers each listed store. Gone once the extension says it is in this browser, and
 * on a phone, where neither store's extension runs. A browser that had it
 * last time shows nothing until the wait is over without a word from it.
 */
export default function ExtensionBanner() {
    const present = useExtensionPresent();
    const [waited, setWaited] = useState(() => !hadExtension());

    useEffect(() => {
        if (present) rememberExtension(true);
    }, [present]);

    useEffect(() => {
        if (waited) return;
        const timer = setTimeout(() => setWaited(true), WAIT_MS);
        return () => clearTimeout(timer);
    }, [waited]);

    useEffect(() => {
        if (waited && !extensionSeen()) rememberExtension(false);
    }, [waited]);

    if (present || !waited) return null;
    return (
        <aside
            aria-labelledby="extension-banner-title"
            className="flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-2xl border border-line bg-card px-4 py-3 shadow-card max-[760px]:hidden"
        >
            <span className="grid size-8 shrink-0 place-items-center rounded-[10px] border border-line bg-card-raised text-brand-ink">
                <PuzzleIcon className="size-[18px]" />
            </span>
            <p className="m-0 flex min-w-0 flex-[1_1_320px] flex-col">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span
                        id="extension-banner-title"
                        className="font-display text-[15px] font-bold tracking-[-0.01em]"
                    >
                        {STRIP_HEADLINE}
                    </span>
                    <span className="rounded-full bg-applied px-2 py-0.5 text-[11px] font-semibold text-applied-fg">
                        {STRIP_PILL}
                    </span>
                </span>
                <span className="text-[13px] text-fg-muted [text-wrap:pretty]">{STRIP_LINE}</span>
            </p>
            <div className="flex flex-wrap gap-2">
                {extensionStores().map((store) => (
                    <a
                        key={store}
                        {...actionProps("extension_install_clicked", {
                            source: "ExtensionBanner",
                            option: store,
                        })}
                        href={EXTENSION_STORES[store]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${OUTLINE_BUTTON} h-9 gap-1.5 rounded-[11px] px-3.5 text-[13px]`}
                    >
                        {store === "chrome" && <ChromeIcon />}
                        {EXTENSION_STORE_LABELS[store]}
                        <ArrowUpRightIcon className="size-3.5" />
                    </a>
                ))}
            </div>
        </aside>
    );
}
